import { useEffect, useRef } from 'react';
import { Palette, ToggleRight, Store, FileText, LogOut, Download, Upload, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { usePlatform } from '../BrandProvider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import BrandEditor from '../BrandEditor';
import FeaturesPanel from './FeaturesPanel';
import StoreCMS from './StoreCMS';
import LegalEditor from './LegalEditor';

const TABS = [['brand', Palette, 'Aspetto'], ['features', ToggleRight, 'Sezioni'], ['store', Store, 'Store'], ['legal', FileText, 'Testi legali']];

const IDLE_MS = 15 * 60 * 1000;

function useIdleExit(onExit) {
  useEffect(() => {
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(() => { toast('Sessione Admin chiusa per inattività (15 min)'); onExit(); }, IDLE_MS); };
    const ev = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    ev.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(t); ev.forEach((e) => window.removeEventListener(e, reset)); };
  }, [onExit]);
}

function BackupButtons() {
  const { setBrand, setFeatures, setLegal } = usePlatform();
  const file = useRef(null);
  const exportJson = async () => {
    try {
      const data = await api('/admin/backup', { auth: true });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
      a.download = `nexora-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      toast.success('Backup scaricato');
    } catch (e) { toast.error(e.message); }
  };
  const importJson = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f || !window.confirm('Ripristinare impostazioni, testi e catalogo da questo backup?')) return;
    try {
      const r = await api('/admin/restore', { method: 'POST', body: JSON.parse(await f.text()), auth: true });
      const [b, p] = await Promise.all([api('/brand'), api('/platform')]);
      setBrand(b); setFeatures(p.features); setLegal(p.legal);
      toast.success(`Backup ripristinato · ${r.games_updated} giochi aggiornati`);
    } catch (x) { toast.error(x instanceof SyntaxError ? 'File JSON non valido' : x.message); }
  };
  const cls = 'flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] text-slate-300 hover:bg-white/5 shrink-0';
  return (
    <>
      <button data-testid="admin-export-btn" aria-label="Esporta backup" title="Esporta backup" onClick={exportJson} className={cls}><Download className="h-3.5 w-3.5" /><span className="hidden md:inline">Esporta backup</span></button>
      <button data-testid="admin-import-btn" aria-label="Ripristina backup" title="Ripristina backup" onClick={() => file.current.click()} className={cls}><Upload className="h-3.5 w-3.5" /><span className="hidden md:inline">Ripristina</span></button>
      <input ref={file} data-testid="admin-import-input" type="file" accept="application/json" className="hidden" onChange={importJson} />
    </>
  );
}

export default function AdminPanel({ onExit, onChangePin }) {
  const tab = sessionStorage.getItem('nexora.admintab') || 'brand';
  useIdleExit(onExit);
  return (
    <div data-testid="admin-panel" className="h-full overflow-y-auto">
      <Tabs defaultValue={tab} onValueChange={(v) => sessionStorage.setItem('nexora.admintab', v)}>
        <div className="sticky top-0 z-20 border-b border-violet-500/15 bg-titan-bg/85 backdrop-blur-xl">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex flex-wrap items-center gap-3">
            <span className="t-label shrink-0 hidden sm:block">Master Control</span>
            <div className="order-last basis-full sm:order-none sm:basis-auto min-w-0 max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabsList className="bg-white/5 w-max justify-start">
                {TABS.map(([id, Icon, label]) => <TabsTrigger key={id} value={id} data-testid={`admin-tab-${id}`} title={label} className="gap-1.5 text-xs shrink-0"><Icon className="h-3.5 w-3.5" />{label}</TabsTrigger>)}
              </TabsList>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <BackupButtons />
              <button data-testid="admin-change-pin" aria-label="Cambia PIN" title="Cambia PIN" onClick={onChangePin} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] text-slate-300 hover:bg-white/5 shrink-0"><KeyRound className="h-3.5 w-3.5" /><span className="hidden md:inline">Cambia PIN</span></button>
              <button data-testid="admin-exit-btn" aria-label="Esci dall'Amministrazione" title="Esci dall'Amministrazione" onClick={onExit} className="flex items-center gap-1.5 rounded-full border border-rose-500/40 px-3 py-1.5 text-[11px] font-semibold text-rose-200 hover:bg-rose-500/10 shrink-0"><LogOut className="h-3.5 w-3.5" /><span className="hidden md:inline">Esci dall'Amministrazione</span></button>
            </div>
          </div>
        </div>
        <TabsContent value="brand" className="mt-0"><BrandEditor /></TabsContent>
        {TABS.slice(1).map(([id, , label]) => (
          <TabsContent key={id} value={id} className="mt-0">
            <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-50 mb-8">{label}<span className="text-cyan-400">.</span></h1>
              {id === 'features' && <FeaturesPanel />}
              {id === 'store' && <StoreCMS />}
              {id === 'legal' && <LegalEditor />}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
